/**
 * The chosen audio output, for code that plays sound without React context.
 *
 * Selecting a speaker only ever affected the lobby's test tone — the single
 * `setSinkId` in the app lived in `speaker-test.service`. So the test played
 * through your headset, which proved the choice had registered, and then the
 * meeting came out of the laptop speakers.
 *
 * Remote voices are routed in their own player, which can read the store. This
 * exists for everything else that makes noise: notification and reaction
 * sounds, the recording announcement — plain `new Audio()` calls in modules
 * with no hook available. Voices in your headset and beeps from the laptop is
 * a worse kind of half-working than either extreme.
 *
 * `setSinkId` is Chromium-only; Firefox and Safari follow the OS output and
 * have nothing to apply, so absence is not an error.
 */

type MediaElementWithSinkId = HTMLMediaElement & {
  setSinkId?: (sinkId: string) => Promise<void>
}

let preferredSpeakerId: string | null = null

/** Mirrors the devices store. Called whenever the selection changes. */
export function setPreferredSpeaker(deviceId: string | null): void {
  preferredSpeakerId = deviceId || null
  // A new choice deserves a fresh verdict.
  reportedBadSink = null
}

/**
 * Point one element at the chosen output. Safe to call on every play: routing
 * an element that is already routed is a no-op.
 *
 * `HTMLMediaElement`, not `HTMLAudioElement`: a shared screen with tab audio
 * plays through the presentation `<video>`, and a `<video>` routes exactly the
 * same way. Typing this to audio elements is what left screen audio on the
 * laptop while every voice went to the headset.
 */
export function applyPreferredSpeaker(element: HTMLMediaElement): void {
  const el = element as MediaElementWithSinkId
  if (!preferredSpeakerId || typeof el.setSinkId !== 'function') return
  // A stream with no audio has nothing to route, and Chromium does not treat
  // it as a no-op: once the player is up it has no audio renderer, so
  // `setSinkId` rejects (AbortError) and the toast below blamed a working
  // speaker. The presentation `<video>` hits this whenever a share's video
  // consumer lands before its audio one. Adding the audio mints a new stream,
  // so the element is routed again then.
  const src = el.srcObject as MediaStream | null
  if (typeof src?.getAudioTracks === 'function' && src.getAudioTracks().length === 0) return
  const requested = preferredSpeakerId
  el.setSinkId(requested).catch(() => {
    // Device unplugged, or output permission refused. The default sink still
    // plays it, which beats silence for a notification — but a chosen output
    // that is quietly not being used is worth saying.
    notifySpeakerRoutingFailure(requested)
  })
}

let reportedBadSink: string | null = null
let onRoutingFailure: ((deviceId: string) => void) | null = null

/**
 * "That output did not work" — reported once per chosen device.
 *
 * Shared by everything that routes sound: the meeting's per-peer players, UI
 * sounds, and the lobby test tone. One dedupe key rather than one per call
 * site, because a headset that has gone away has gone away for all of them and
 * a toast per remote peer is its own kind of unhelpful. Picking a different
 * speaker clears it, so the next failure is reported.
 */
export function notifySpeakerRoutingFailure(deviceId: string): void {
  if (!deviceId || reportedBadSink === deviceId) return
  reportedBadSink = deviceId
  onRoutingFailure?.(deviceId)
}

/**
 * Where those reports go.
 *
 * A callback rather than a direct toast call: this module is imported by the
 * devices store, and reaching from there into the UI toolkit is a dependency
 * pointing the wrong way. The app wires it once at startup.
 */
export function setSpeakerRoutingFailureHandler(
  handler: ((deviceId: string) => void) | null,
): void {
  onRoutingFailure = handler
}
